import React, { useState } from "react";
import { Box, Tabs, Tab, Divider, Typography } from "@mui/material";
import Debug from "./Debug";
import Feedback from "../../components/Feedback/Feedback";
import Question from "./Question";
import Answer from "./Answer/Answer";
import ShareMessage from "./ShareMessage";
import Usage from "./Usage";
import AnalysisLogs from "../../components/AnalysisLogs";
import Bookmark from "./Bookmark";
import SourcesList from "../../components/SourcesList";
import { makeStyles } from "tss-react/mui";
import { usePostHog } from "posthog-js/react";
import { CLICK_TAB_ON_MESSAGE } from "../../../../../utils/posthogEvents";
import DownloadEntity from "./DownloadEntity";
import CopyMessageLink from "./CopyMessageLink";
import {
  AssistantRounded,
  MediationRounded,
  FactCheckRounded,
  AssistantOutlined,
  MediationOutlined,
  FactCheckOutlined,
} from "@mui/icons-material";
import RelatedDocs from "./RelatedDocs/RelatedDocs";

const useStyles = makeStyles()((theme, _params, classes) => ({
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  customTabs: {
    minHeight: 36,
    marginTop: 2,
    "& .MuiTabs-indicator": {
      height: 4,
      borderRadius: "50px 50px 0px 0px",
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
    "& .MuiTab-root": {
      minHeight: 36,
      paddingTop: 6,
      paddingBottom: 6,
    },
    "& .MuiTabs-flexContainer": {
      minHeight: 36,
      paddingTop: 6,
      paddingBottom: 4,
    },
    "& .MuiTab-root.Mui-selected": {
      color: theme.palette.mode === "dark" ? theme.palette.primary.main : "#9C27B0",
    },
  },
}));

function Message({
  selectedFileFromStream,
  onSelectedFileFromStreamChange,
  tabular,
  message,
  messageRefs,
  relatedDocsFromStreaming = [],
  handleEntityChange,
  handleTabularChange,
  isLastMessage,
  queryContainerHeight,
  isStreaming = false,
  isFirstMessage = false,
  numOfMessages,
  selectedSource,
  setSelectedSource,
  isMetadataLoading = false,
  saved = false,
  onMessageUpdate = () => {},
  selectedRelatedDoc,
  setSelectedRelatedDoc,
  isolationMarkup,
  onIsolationMarkupChange,
  selectedIsolationMarkup,
  onSelectedIsolationMarkupChange,
  lastStreamedMessageId,
}) {
  const posthog = usePostHog();
  const { classes } = useStyles();
  const { query, answer } = message;
  const isDataAnalysis = answer["content_object"]["type"] === "tabular_data";
  const isEntityResponse = answer["content_object"]["type"] === "entity";
  const [selectedTab, setSelectedTab] = useState("ai_answer");
  const [showToolbar, setShowToolbar] = useState(false);
  const [showGeneralDomainAnswer, setShowGeneralDomainAnswer] = useState(false);

  // Get citations count for the Sources tab badge (count unique files, not individual citations)
  const citations = answer["content_object"]["citations"] || [];
  const uniqueFileIds = [
    ...new Set(citations.map((citation) => citation.file_id)),
  ];
  const sourcesCount = uniqueFileIds.length;

  const isAnswerFound = answer["content_object"]["answer_found"];
  const isStreamAPIError =
    answer["error"] && answer["error"] === "stream_api_fail";

  // Determine if Related Docs exist (from persisted message or streaming fallback)
  const hasPersistedRelatedDocs = Array.isArray(
    message?.answer_rd?.content_object?.related_docs
  )
    ? message.answer_rd.content_object.related_docs.length > 0
    : false;

  const hasStreamingRelatedDocs = Boolean(
    isLastMessage &&
      !hasPersistedRelatedDocs &&
      Array.isArray(relatedDocsFromStreaming) &&
      relatedDocsFromStreaming.length > 0
  );

  const hasRelatedDocs = hasPersistedRelatedDocs || hasStreamingRelatedDocs;

  const handleClickTab = (tab) => {
    setSelectedTab(tab);
    posthog.capture(CLICK_TAB_ON_MESSAGE, {
      tab: tab,
    });
  };

  return (
    <Box
      ref={(el) => (messageRefs.current[query["message_id"]] = el)}
      sx={{
        minHeight:
          isLastMessage && !isStreaming
            ? `calc(100vh - ${saved ? 113 : 197 + queryContainerHeight}px)`
            : "inherit",
      }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 999,
          background: (theme) => theme.palette.background.default,
          pt: 3,
        }}>
        <Question query={query} />
        <Box
          mt={2}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}>
          <Tabs
            value={selectedTab}
            onChange={(_, value) => {
              handleClickTab(value);
            }}
            className={classes.customTabs}>
            <Tab
              label="AI Answer"
              value={"ai_answer"}
              iconPosition="start"
              icon={
                selectedTab === "ai_answer" ? (
                  <AssistantRounded fontSize="small" />
                ) : (
                  <AssistantOutlined fontSize="small" />
                )
              }
            />
            {!isEntityResponse && isAnswerFound && (
              <Tab
                label={`Sources (${sourcesCount})`}
                value={"sources"}
                iconPosition="start"
                icon={
                  selectedTab === "sources" ? (
                    <MediationRounded fontSize="small" />
                  ) : (
                    <MediationOutlined fontSize="small" />
                  )
                }
              />
            )}
            {hasRelatedDocs && (
              <Tab
                label="Similar Files"
                value={"related_docs"}
                iconPosition="start"
                icon={
                  selectedTab === "related_docs" ? (
                    <FactCheckRounded fontSize="small" />
                  ) : (
                    <FactCheckOutlined fontSize="small" />
                  )
                }
              />
            )}
          </Tabs>
          <Box>{isDataAnalysis ? <AnalysisLogs answer={answer} /> : null}</Box>
        </Box>
        <Divider />
        <Box mb={3} />
      </Box>
      {/* Tab content */}
      <Box
        onMouseEnter={() => setShowToolbar(true)}
        onMouseLeave={() => setShowToolbar(false)}>
        {selectedTab === "ai_answer" && (
          <>
            {/* Check if we should show GeneralDomainAnswer instead of regular Answer */}
            {(() => {
              const citations = answer["content_object"]["citations"] || [];
              const entity = answer["content_object"]["entity"];
              const entityExists =
                entity && entity["file_id"] && entity["title"];
              const hasNoSources = citations.length === 0 && !entityExists;
              const messageText = answer["content_object"]["text"] || "";
              const hasContentButNoSources =
                messageText.trim().length > 0 && hasNoSources;
              const shouldShowGeneralDomainPrompt =
                !showGeneralDomainAnswer &&
                hasContentButNoSources &&
                isAnswerFound;
              // &&
              // !isMetadataLoading &&
              // !isStreaming;

              if (isStreamAPIError) {
                return (
                  <Typography>
                    Sorry, there was a problem in loading the answer
                  </Typography>
                );
              }

              // Commented out: General domain answer prompt when no sources are found
              // if (shouldShowGeneralDomainPrompt) {
              //   return (
              //     <GeneralDomainAnswer
              //       showGeneralDomainAnswer={showGeneralDomainAnswer}
              //       setShowGeneralDomainAnswer={setShowGeneralDomainAnswer}
              //     />
              //   );
              // }

              return (
                <Answer
                  answer={answer}
                  entity={entity}
                  tabular={tabular}
                  handleEntityChange={handleEntityChange}
                  handleTabularChange={handleTabularChange}
                  setSelectedSource={setSelectedSource}
                  showGeneralDomainAnswer={showGeneralDomainAnswer}
                  setShowGeneralDomainAnswer={setShowGeneralDomainAnswer}
                  isMetadataLoading={isMetadataLoading}
                  isStreaming={false}
                  selectedFileFromStream={selectedFileFromStream}
                  onSelectedFileFromStreamChange={
                    onSelectedFileFromStreamChange
                  }
                  isolationMarkup={isolationMarkup}
                  onIsolationMarkupChange={onIsolationMarkupChange}
                  selectedIsolationMarkup={selectedIsolationMarkup}
                  onSelectedIsolationMarkupChange={onSelectedIsolationMarkupChange}
                  lastStreamedMessageId={lastStreamedMessageId}
                />
              );
            })()}
          </>
        )}
        {selectedTab === "sources" && !isEntityResponse && isAnswerFound && (
          <SourcesList
            message={message}
            setSelectedSource={setSelectedSource}
            selectedSource={selectedSource}
          />
        )}
        {selectedTab === "related_docs" && hasRelatedDocs && (
          <RelatedDocs
            message={message}
            selectedRelatedDoc={selectedRelatedDoc}
            setSelectedRelatedDoc={setSelectedRelatedDoc}
            relatedDocsFromStreaming={
              isLastMessage &&
              (!message?.answer_rd ||
                !(message?.answer_rd?.content_object?.related_docs?.length > 0))
                ? relatedDocsFromStreaming
                : []
            }
          />
        )}
        <Box
          width={"100%"}
          mt={!answer["error"] || (isFirstMessage && numOfMessages < 2) ? 4 : 0}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}>
          <Box />
          {!answer["error"] && (
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                opacity: showToolbar || isLastMessage ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
                position: "relative",
                zIndex: 1,
                pointerEvents: showToolbar || isLastMessage ? "auto" : "none",
              }}>
              <Feedback message={message} />
              {isEntityResponse ? (
                <CopyMessageLink query={query} />
              ) : (
                <ShareMessage message={message} />
              )}
              {isEntityResponse && <DownloadEntity answer={answer} />}
              <Debug query={query} />
              <Bookmark message={message} onMessageUpdate={onMessageUpdate} />
              <Usage isLastMessage={isLastMessage} />
            </Box>
          )}
        </Box>
      </Box>
      {(!isLastMessage || isStreaming) && <Box mb={7.5} />}
    </Box>
  );
}

export default Message;
